import { FC, ReactNode, useEffect } from 'react';
import { Box, Container } from "@chakra-ui/react"
import { NextPage } from 'next';
import { Header } from '../shared';
import Head from 'next/head';
import { HelpProvider } from 'domains/help-tour';
import { CustomDragLayer } from 'domains/drag-and-drop';
import { UpgradeProvider } from 'domains/subscription';
import { ProgressProvider } from 'lib/context/progressContext';
import { DropFileCatchAllWrapper } from 'components';
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import { isTouchDevice, isReadOnly } from "lib/utils";
import { GetPackResponseData } from 'lib/clients/client-packs/types/PacksClient';
import { useRouter } from "next/router";
import { useRunSync, useStickyAd, usePack, usePackAssets } from "lib/hooks";
import { PackProvider } from 'domains/pack';

type Props = {
  prefetchedPack: GetPackResponseData;
}

const PackLayout: FC<Props> = ({ prefetchedPack, children }) => {
  const router = useRouter();
  const { data: pack } = usePack(router.query.id as string);
  const { data: assets } = usePackAssets(pack);
  const { displayAd } = useStickyAd(router.query.id as string);
  const { sync } = useRunSync(pack?.id);

  const isPackReadOnly = isReadOnly(pack);

  useEffect(() => {
    if (isPackReadOnly) return;

    const interval = setInterval(async () => {
      if (isPackReadOnly) return;
      await sync();
    }, 10000);

    return () => {
      clearInterval(interval)
    };
  }, [pack, assets, sync, isPackReadOnly]);

  const backend = isTouchDevice() ? TouchBackend : HTML5Backend;
  const displayPack = pack || prefetchedPack;

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{ displayPack?.title } | Everything in one link</title>
        <meta name="description" content="Collect and curate everything on a subject. Add context to each file. Pack it all up in one master link. Then email the link, share it on Slack, pass it around your team. And put an end to losing + explaining assets" />
        {!displayPack?.index_enabled && <meta name="robots" content="noindex,nofollow" />}
      </Head>
      <DndProvider backend={backend}>
        <CustomDragLayer />
        <HelpProvider>
          <PackProvider value={{ packId: pack?.id }}>
            <DropFileCatchAllWrapper>
              <Header />
              <Box width="100%" pt={['140px', '140px', '95px']} pb={displayAd && !isReadOnly ? [316, 366, 246, 236] : 120}>
                <Container maxWidth={1500} id="pack-wrapper" className="pack-wrapper">
                  {children}
                </Container>
              </Box>
            </DropFileCatchAllWrapper>
          </PackProvider>
        </HelpProvider>
      </DndProvider>
    </>
  )
};

/**
 * Enables page component to tell which layout it needs and delegate
 * the logic into this function
 * @param   {NextPage}  page  A next.js component
 * @return  {ReactNode}       A next page with it's declared layout
 */
export const getLayout = (page: NextPage, pageProps: any): ReactNode => (
  <UpgradeProvider>
    <ProgressProvider>
      <PackLayout {...pageProps}>{page}</PackLayout>
    </ProgressProvider>
  </UpgradeProvider>
)

export default PackLayout;
